import { createContext as createReactContext, useContext, useReducer, type Dispatch, type ReactNode } from 'react';
import { createContext as createSelectableContext, useContextSelector } from 'use-context-selector';

import {
  alternativeExercisesReducer,
  initialAlternativeExercisesState,
  type AlternativeExercisesState,
} from 'state/alternative-exercises';
import { authReducer, initialAuthState, type AuthState } from 'state/auth';
import { cookieConsentReducer, initialCookieConsentState, type CookieConsentState } from 'state/cookie-consent';
import { initialLanguageState, languageReducer, type LanguageState } from 'state/language';
import { initialOnboardState, onboardReducer, type OnboardState } from 'state/onboard';
import { initialTimerState, timerReducer, type TimerState, type TimerAction } from 'state/timer';
import { initialUserState, userReducer, type UserState } from 'state/user';

import type { AllActions } from 'actions';

type State = {
  auth: AuthState;
  onboard: OnboardState;
  language: LanguageState;
  alternativeExercises: AlternativeExercisesState;
  cookieConsent: CookieConsentState;
  timer: TimerState;
  user: UserState;
};

const combinedReducer = (
  { auth, onboard, language, alternativeExercises, cookieConsent, timer, user }: State,
  action: AllActions,
) => ({
  auth: authReducer(auth, action),
  onboard: onboardReducer(onboard, action),
  language: languageReducer(language, action),
  alternativeExercises: alternativeExercisesReducer(alternativeExercises, action),
  cookieConsent: cookieConsentReducer(cookieConsent, action),
  timer: timerReducer(timer, action as TimerAction),
  user: userReducer(user, action),
});

const initialState: State = {
  auth: initialAuthState,
  onboard: initialOnboardState,
  language: initialLanguageState,
  alternativeExercises: initialAlternativeExercisesState,
  cookieConsent: initialCookieConsentState,
  timer: initialTimerState,
  user: initialUserState,
};

export const StateContext = createSelectableContext<State>(initialState);
type Selector<T> = (state: State) => T;
export function useAppState<T>(selector: Selector<T>): T {
  return useContextSelector(StateContext, selector);
}

const initialDispatch: Dispatch<AllActions> = () => null;
export const DispatchContext = createReactContext(initialDispatch);
export const useDispatch = () => useContext(DispatchContext);

export const StateProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(combinedReducer, initialState);

  return (
    <StateContext.Provider value={state}>
      <DispatchContext.Provider value={dispatch}>{children}</DispatchContext.Provider>
    </StateContext.Provider>
  );
};
